/**
 * The MCP tool chooser (FEATURE-20261101-mcp-url-tool-chooser): the tools a URL or an issued auth
 * offers, as a tree of checkboxes. continuum's own tools by family, then one group per app
 * (mcp.v1.ListTools). Checking a whole group stores its entry ("continuum.*", "tracker.v1.*"), so a
 * tool added to it later is included; "Every tool" stores the empty set. whoami is always offered.
 */
import { useEffect, useState } from "react";
import { Box, Checkbox, Chip, Collapse, FormControlLabel, IconButton, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { listMcpTools } from "../../services/mcp-url-client";
import type { McpToolGroup, McpToolInfo } from "../../services/mcp-url-client";
import { allows, leaves, otherEntries, toggle } from "./tool-set";

export type ToolChooserProps = {
  value: string[];
  onChange: (set: string[]) => void;
};

export function ToolChooser({ value, onChange }: ToolChooserProps) {
  const [tree, setTree] = useState<McpToolGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    listMcpTools()
      .then(setTree)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  if (error) return <Typography color="error">Could not list the tools: {error}</Typography>;
  if (!tree) return <Typography color="text.secondary">Loading the tools…</Typography>;

  const every = value.length === 0;
  const others = every ? [] : otherEntries(value, tree);
  const set = (tools: McpToolInfo[], on: boolean) => onChange(toggle(value, tree, tools, on));

  return (
    <Box data-testid="mcp-tool-chooser">
      <FormControlLabel
        control={
          <Checkbox
            checked={every}
            onChange={(e) => onChange(e.target.checked ? [] : toggle([], tree, [], true))}
          />
        }
        label={
          <span>
            <b>Every tool</b>, including tools added later
          </span>
        }
      />
      <Box sx={{ pl: 1, opacity: every ? 0.5 : 1, pointerEvents: every ? "none" : "auto" }}>
        {tree.map((g) => (
          <GroupNode key={g.name} group={g} value={value} depth={0} onToggle={set} />
        ))}
      </Box>
      {others.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Also in this set (not in the tree):
          </Typography>{" "}
          {others.map((o) => (
            <Chip key={o} label={o} size="small" sx={{ mr: 0.5 }} onDelete={() => onChange(value.filter((v) => v !== o))} />
          ))}
        </Box>
      )}
    </Box>
  );
}

function GroupNode({
  group,
  value,
  depth,
  onToggle,
}: {
  group: McpToolGroup;
  value: string[];
  depth: number;
  onToggle: (tools: McpToolInfo[], on: boolean) => void;
}) {
  const [open, setOpen] = useState(depth === 0);
  const all = leaves(group).filter((t) => !t.alwaysOffered);
  const on = all.filter((t) => allows(value, t)).length;
  const checked = all.length > 0 && on === all.length;
  const partial = on > 0 && !checked;
  return (
    <Box sx={{ pl: depth === 0 ? 0 : 3 }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <IconButton size="small" onClick={() => setOpen(!open)} aria-label={open ? "collapse" : "expand"}>
          {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={checked}
              indeterminate={partial}
              disabled={all.length === 0}
              onChange={(e) => onToggle(all, e.target.checked)}
            />
          }
          label={
            <span>
              <b>{group.label || group.name}</b>
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {on}/{all.length}
              </Typography>
            </span>
          }
        />
      </Box>
      <Collapse in={open}>
        <Box sx={{ pl: 4 }}>
          {(group.tools ?? []).map((t) => (
            <Box key={t.name} sx={{ display: "flex", alignItems: "flex-start" }}>
              <FormControlLabel
                sx={{ alignItems: "flex-start", my: 0.25 }}
                control={
                  <Checkbox
                    size="small"
                    sx={{ pt: 0.25 }}
                    checked={allows(value, t)}
                    disabled={t.alwaysOffered}
                    onChange={(e) => onToggle([t], e.target.checked)}
                  />
                }
                label={
                  <span>
                    <code>{t.name}</code>
                    {t.alwaysOffered && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        always offered
                      </Typography>
                    )}
                    {t.description && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        {t.description}
                      </Typography>
                    )}
                  </span>
                }
              />
            </Box>
          ))}
        </Box>
        {(group.groups ?? []).map((g) => (
          <GroupNode key={g.name} group={g} value={value} depth={depth + 1} onToggle={onToggle} />
        ))}
      </Collapse>
    </Box>
  );
}
